/**
 * Console Event Logger
 * Captures important console events and sends them to backend for debugging
 * Uses a dedicated logger object instead of intercepting global console
 */

const consoleEventLoggerEnabled = process.env.REACT_APP_REMOTE_DEBUG === '1';
const baseUrl = process.env.REACT_APP_BASE_URL;

let consoleSessionId = null;
const BREADCRUMB_LIMIT = 15;
const breadcrumbs = [];

/**
 * Format arguments safely for JSON serialization
 */
const formatArgs = (args) => {
  if (!args || args.length === 0) return [];

  return args.map((arg) => {
    try {
      if (arg === null) return 'null';
      if (arg === undefined) return 'undefined';
      if (typeof arg === 'string' || typeof arg === 'number' || typeof arg === 'boolean') {
        return arg;
      }
      if (arg instanceof Error) {
        return {
          type: 'Error',
          message: arg.message,
          stack: arg.stack,
        };
      }
      if (typeof arg === 'object') {
        return JSON.stringify(arg, null, 2).substring(0, 500); // Limit to 500 chars
      }
      return String(arg).substring(0, 500);
    } catch (e) {
      return '[Unable to serialize]';
    }
  });
};

const addBreadcrumb = (level, message, args) => {
  breadcrumbs.push({
    level,
    message,
    timestamp: new Date().toISOString(),
    args: formatArgs(args),
  });
  if (breadcrumbs.length > BREADCRUMB_LIMIT) breadcrumbs.shift();
};

/**
 * Send console event to backend
 */
const sendConsoleEvent = async (level, message, args) => {
  if (!consoleEventLoggerEnabled || !consoleSessionId) return;

  try {
    const payload = {
      session_id: consoleSessionId,
      event_type: `remote_${level}`,
      severity: level === 'error' ? 'error' : 'warning',
      component_name: 'Console',
      description: message,
      event_data: {
        level,
        message,
        args: formatArgs(args),
        breadcrumbs: [...breadcrumbs], // Include breadcrumbs context
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
      },
    };

    await fetch(`${baseUrl}debug/event`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    // Silently fail to avoid disrupting the app
    console.error('Failed to send remote log:', error);
  }
};

/**
 * Initialize console event logging
 * Call this after Redux store is initialized to get the session ID
 */
export const initConsoleEventLogger = (sessionId) => {
  if (!consoleEventLoggerEnabled) return;

  consoleSessionId = sessionId;

  // Catch unhandled exceptions
  window.addEventListener('error', (event) => {
    const errorInfo = {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error ? {
        message: event.error.message,
        stack: event.error.stack
      } : null
    };
    sendConsoleEvent('error', `Unhandled Exception: ${event.message}`, [errorInfo]);
  });

  // Catch unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    sendConsoleEvent('error', `Unhandled Rejection: ${event.reason}`, [event.reason]);
  });
};

/**
 * Dedicated logger object for explicit logging to backend
 */
export const logger = {
  info: (message, ...args) => {
    console.info(message, ...args);
    addBreadcrumb('info', message, args);
  },
  log: (message, ...args) => {
    console.log(message, ...args);
    addBreadcrumb('info', message, args);
  },
  warn: (message, ...args) => {
    console.warn(message, ...args);
    addBreadcrumb('warn', message, args);
    sendConsoleEvent('warn', message, args);
  },
  error: (message, ...args) => {
    console.error(message, ...args);
    addBreadcrumb('error', message, args);
    sendConsoleEvent('error', message, args);
  },
  debug: (message, ...args) => {
    console.debug(message, ...args);
    addBreadcrumb('debug', message, args);
  },
  group: (label) => {
    console.group(label);
    addBreadcrumb('info', `GROUP: ${label}`, []);
  },
  groupEnd: () => {
    console.groupEnd();
  }
};

export default initConsoleEventLogger;
